import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { API_URL, ApiError } from "../api";
import { EmailFrame } from "../components/EmailFrame";
import { Logo } from "../components/Logo";
import { renderMarkdown } from "../markdown";

interface PublicNote {
  title: string;
  bodyText: string;
  bodyHtml: string | null;
  format: "text" | "markdown";
  createdAt: string;
  updatedAt: string;
}

/** Read-only view of a shared note, for anyone with the link. No account, no cookies needed. */
export function Shared({ token }: { token: string }) {
  const note = useQuery({
    queryKey: ["public", token],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/public/notes/${encodeURIComponent(token)}`);
      if (!res.ok) throw new ApiError(res.status, "not found");
      return (await res.json()) as PublicNote;
    },
    retry: false,
    refetchOnWindowFocus: false,
  });

  // Shared notes are unlisted: keep them out of search engines.
  useEffect(() => {
    const meta = Object.assign(document.createElement("meta"), { name: "robots", content: "noindex, nofollow" });
    document.head.append(meta);
    return () => meta.remove();
  }, []);

  useEffect(() => {
    document.title = note.data ? `${note.data.title || "Note"} · ccnote` : "ccnote";
  }, [note.data]);

  const html = useMemo(
    () => (note.data?.format === "markdown" && !note.data.bodyHtml ? renderMarkdown(note.data.bodyText) : null),
    [note.data],
  );

  return (
    <div className="shared-page">
      <header className="shared-top">
        <a href="https://ccnote.me" className="shared-brand">
          <Logo size={26} />
        </a>
        <a className="btn btn-small" href="https://ccnote.me">
          What's ccnote?
        </a>
      </header>

      <main className="shared-card">
        {note.isPending ? (
          <p className="muted">Loading…</p>
        ) : note.isError || !note.data ? (
          <div className="shared-missing">
            <h1>This link isn't available</h1>
            <p className="muted">It may have been turned off by the person who shared it, or the note was removed.</p>
          </div>
        ) : (
          <article>
            <h1 className="shared-title">{note.data.title || "Untitled"}</h1>
            <p className="muted small">
              Shared note · updated {new Date(note.data.updatedAt).toLocaleDateString(undefined, { dateStyle: "long" })}
            </p>
            {note.data.bodyHtml ? (
              <EmailFrame html={note.data.bodyHtml} />
            ) : html !== null ? (
              <div className="markdown-body shared-body" dangerouslySetInnerHTML={{ __html: html }} />
            ) : (
              <div className="shared-body plain-body">{note.data.bodyText}</div>
            )}
          </article>
        )}
      </main>

      <footer className="shared-foot">
        Shared with <a href="https://ccnote.me">ccnote</a>: CC an email to your own address and it becomes a note.
      </footer>
    </div>
  );
}
